import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import Honeybadger from '@honeybadger-io/js';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import { clearSelfSchedulingPersistence } from 'lib/helpers/draftCrypto';

import { userState } from 'components/state';
import Icon from 'components/shared/Icon/Icon';
import Avatar from 'components/shared/Avatar/Avatar';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

const LoginCard = ({ onOpenLoginModal }) => {
  const [user, setUser] = useRecoilState(userState);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (!user?.logged_in) {
    return (
      <button
        type="button"
        onClick={onOpenLoginModal}
        className="text-inherit flex w-full cursor-pointer items-center gap-3 rounded-xl border-none bg-white px-5 py-4 transition-colors hover:bg-neutral-50">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100">
          <Icon icon="arrow-right-mono" className="h-3 w-3 text-neutral-600" />
        </div>
        <span className="text-[14px] font-medium text-neutral-700">
          Existing patient? <span className="text-primary-500 hover:underline">Log in</span>
        </span>
      </button>
    );
  }

  const fullName = [user.f_name, user.l_name].filter(Boolean).join(' ') || 'Patient';

  const handleLogout = async () => {
    setIsLoggingOut(true);
    const onLogoutError = (error) => {
      Honeybadger.notify(error);
      showAlert({ color: 'danger', message: "Couldn't log out. Please try again." });
      setIsLoggingOut(false);
    };

    await requestApi({
      url: '/api/user/logout',
      navigate,
      params: {},
      onSuccess: async () => {
        sessionStorage.removeItem('myriad-session-reference-id');
        queryClient.clear();
        // Drop the self-scheduling draft + its IDB key so the next patient on
        // this browser can't resume the previous patient's wizard.
        await clearSelfSchedulingPersistence({ onError: (e) => Honeybadger.notify(e) });
        setUser({
          user_id: '',
          f_name: '',
          l_name: '',
          p_name: '',
          email: '',
          state: '',
          kind: '',
          logged_in: false,
          profile_photo: null
        });
        navigate('/login');
      },
      onError: onLogoutError,
      onCatch: onLogoutError
    });
  };

  return (
    <>
      <div className="flex w-full items-center gap-1.5 rounded-xl bg-white px-3 py-2 shadow-[0px_0px_16px_0px_rgba(65,70,81,0.08)]">
        <Avatar
          firstName={user.f_name}
          lastName={user.l_name}
          id={user.user_id}
          image={user.profile_photo}
          size="medium"
          isSquare={false}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="truncate text-[12px] font-medium leading-[14px] text-neutral-700">
            {fullName}
          </p>
          <p className="truncate text-[12px] font-medium leading-[14px] text-neutral-500">
            {user.email}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowLogoutConfirm(true)}
          className="flex flex-shrink-0 cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[12px] font-medium leading-[14px] text-neutral-700 hover:text-primary-500">
          Log out
          <Icon icon="logout-2-mono" size={20} />
        </button>
      </div>
      <Confirm
        isOpen={showLogoutConfirm}
        handleClose={() => !isLoggingOut && setShowLogoutConfirm(false)}
        title="Log out"
        message="If you log out, you can keep booking, but you'll need to re-enter your personal info and use a different email address."
        icon="logout-2-mono"
        variant="warning"
        footerActions={{
          primary: {
            text: 'Log out',
            onClick: handleLogout,
            color: 'primary',
            loading: isLoggingOut,
            disabled: isLoggingOut
          }
        }}
      />
    </>
  );
};

export default LoginCard;
